var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback } from 'react';
import { createBridgeChannel } from './createBridgeChannel';
import { AGENT_PROMPT_CHANNEL, } from '../types/agentPromptChannel';
/**
 * Cross-app channel for sending a prompt to the shell-hosted Robin Assistant,
 * built on the generic {@link createBridgeChannel} primitive. See
 * `types/agentPromptChannel.ts` for the payload contract and the
 * backwards-compatibility policy.
 *
 * One-directional (host apps emit, the shell subscribes), so the surface is
 * two hooks and nothing else. The assistant's replies are not routed back
 * here: they render in the chat, where the user is already looking.
 */
var channel = createBridgeChannel(AGENT_PROMPT_CHANNEL);
/**
 * Mint an id for one request. `crypto.randomUUID` needs a secure context, so
 * it is absent over plain http (some local dev setups) and in older jsdom;
 * the fallback only has to be unique within a session, because that is the
 * whole lifetime of the receiver's dedupe set.
 */
var newRequestId = function () {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : "agent-prompt-".concat(Date.now(), "-").concat(Math.random().toString(36).slice(2));
};
/**
 * Returns a stable callback that asks the assistant to run `prompt`.
 *
 * The shell opens the assistant sider if it is closed, so callers do not need
 * to. Pass `displayText` when `prompt` carries context the user should not
 * have to read back — the bubble shows `displayText`, the model gets `prompt`.
 *
 * No-ops when the shell is too old to listen, and does nothing visible when
 * the assistant is disabled for the org; there is no delivery receipt, by
 * design — the user sees the answer arrive in the sider.
 */
export function useSendAgentPrompt() {
    var emit = channel.useEmit();
    return useCallback(function (prompt, displayText) {
        if (!prompt.trim()) {
            return;
        }
        emit(__assign(__assign({ prompt: prompt }, (displayText ? { displayText: displayText } : {})), { requestId: newRequestId() }));
    }, [emit]);
}
/**
 * Subscribe to prompt requests from host apps. Shell only — MFEs send, the
 * shell is the single receiver that owns the assistant.
 */
export function useAgentPromptRequests(listener) {
    channel.useSubscribe(listener);
}
